import { ArrowLeft, GripVertical, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { DndContext, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MultiImageUpload } from "@/components/admin/ImageUpload";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/lib/api";

const EMPTY = {
  name: "", description: "", price: "", sale_price: "", images: [], category_id: "",
  featured: false, in_stock: true, active: true, order: 0, stock: "", variations: [], addons: [],
};

function SortableFieldRow({ id, testid, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, position: "relative", zIndex: isDragging ? 10 : undefined }}
      className="flex gap-2 items-center"
      data-testid={testid}
    >
      <button type="button" className="cursor-grab touch-none text-muted-foreground hover:text-foreground shrink-0" {...attributes} {...listeners} aria-label="Drag to reorder" data-testid={`${testid}-handle`}>
        <GripVertical className="h-4 w-4" />
      </button>
      {children}
    </div>
  );
}

export default function AdminProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(id);

  useEffect(() => {
    API.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
    if (isEdit) {
      API.get("/admin/products").then((r) => {
        const p = r.data.find((x) => x.id === id);
        if (p) {
          setForm({
            ...EMPTY, ...p,
            price: p.price ?? "", sale_price: p.sale_price ?? "", stock: p.stock ?? "",
            variations: p.variations || [], addons: p.addons || [], images: p.images || [],
          });
        } else {
          toast.error("Product not found");
          navigate("/admin/products");
        }
      });
    }
  }, [id, isEdit, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } })
  );
  const dragList = (listKey) => ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setForm((f) => ({ ...f, [listKey]: arrayMove(f[listKey], Number(active.id), Number(over.id)) }));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Product name is required");
    if (form.price === "" || isNaN(Number(form.price))) return toast.error("A valid price is required");
    const payload = {
      ...form,
      price: Number(form.price),
      sale_price: form.sale_price === "" ? null : Number(form.sale_price),
      order: Number(form.order) || 0,
      stock: form.stock === "" || form.stock === null || form.stock === undefined ? null : Math.max(0, parseInt(form.stock, 10) || 0),
      variations: form.variations.filter((v) => v.name).map((v) => ({ name: v.name, price: Number(v.price) || 0 })),
      addons: form.addons.filter((a) => a.name).map((a) => ({ name: a.name, price: Number(a.price) || 0 })),
    };
    setSaving(true);
    try {
      const attempt = () => (isEdit ? API.patch(`/admin/products/${id}`, payload) : API.post("/admin/products", payload));
      try {
        await attempt();
      } catch (firstErr) {
        // transient proxy/edge failures (rare 404/502 hiccups) — retry once before giving up;
        // genuine 4xx validation errors fail immediately
        const status = firstErr.response?.status;
        if (status && status >= 400 && status < 500) throw firstErr;
        await new Promise((r) => setTimeout(r, 1500));
        await attempt();
      }
      toast.success(isEdit ? "Product updated" : "Product created");
      navigate("/admin/products");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail || "Couldn't save — the connection hiccuped. Your changes are still here, please try again.");
    } finally {
      setSaving(false);
    }
  };

  const catOptions = categories.map((c) => ({
    ...c,
    label: c.parent_id ? `— ${c.name}` : c.name,
  }));

  return (
    <div data-testid="admin-product-form">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/admin/products")} data-testid="product-form-back-btn" aria-label="Back"><ArrowLeft className="h-5 w-5" /></Button>
        <h1 className="font-display text-3xl tracking-tight">{isEdit ? "Edit product" : "New product"}</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardContent className="p-6 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="pf-name">Name *</Label>
                <Input id="pf-name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="product-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pf-desc">Description</Label>
                <Textarea id="pf-desc" rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="product-description-input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pf-price">Price (£) *</Label>
                  <Input id="pf-price" type="number" step="0.01" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="product-price-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pf-sale">Sale price (£, optional)</Label>
                  <Input id="pf-sale" type="number" step="0.01" min="0" value={form.sale_price} onChange={(e) => set("sale_price", e.target.value)} data-testid="product-sale-price-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Images</Label>
                <MultiImageUpload images={form.images} onChange={(v) => set("images", v)} testId="product-images-upload" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl">Variations</h2>
                <Button variant="outline" size="sm" onClick={() => set("variations", [...form.variations, { name: "", price: "" }])} data-testid="add-variation-btn">
                  <Plus className="mr-1 h-4 w-4" /> Add
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mb-4">e.g. Standard / Large / Deluxe, or colour options. Variations set their own price.</p>
              <div className="space-y-3">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={dragList("variations")}>
                  <SortableContext items={form.variations.map((_, i) => i)} strategy={verticalListSortingStrategy}>
                    {form.variations.map((v, i) => (
                      <SortableFieldRow key={i} id={i} testid={`variation-row-${i}`}>
                        <Input placeholder="Name (e.g. Large)" value={v.name} onChange={(e) => set("variations", form.variations.map((x, xi) => xi === i ? { ...x, name: e.target.value } : x))} data-testid={`variation-name-${i}`} />
                        <Input placeholder="Price" type="number" step="0.01" className="w-28" value={v.price} onChange={(e) => set("variations", form.variations.map((x, xi) => xi === i ? { ...x, price: e.target.value } : x))} data-testid={`variation-price-${i}`} />
                        <Button variant="ghost" size="icon" className="text-destructive shrink-0" onClick={() => set("variations", form.variations.filter((_, xi) => xi !== i))} aria-label="Remove variation"><Trash2 className="h-4 w-4" /></Button>
                      </SortableFieldRow>
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl">Add-ons</h2>
                <Button variant="outline" size="sm" onClick={() => set("addons", [...form.addons, { name: "", price: "" }])} data-testid="add-addon-btn">
                  <Plus className="mr-1 h-4 w-4" /> Add
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mb-4">Optional extras customers can tick, e.g. chocolates or a vase. Set price to 0 for free extras.</p>
              <div className="space-y-3">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={dragList("addons")}>
                  <SortableContext items={form.addons.map((_, i) => i)} strategy={verticalListSortingStrategy}>
                    {form.addons.map((a, i) => (
                      <SortableFieldRow key={i} id={i} testid={`addon-row-${i}`}>
                        <Input placeholder="Name (e.g. Box of chocolates)" value={a.name} onChange={(e) => set("addons", form.addons.map((x, xi) => xi === i ? { ...x, name: e.target.value } : x))} data-testid={`addon-name-${i}`} />
                        <Input placeholder="Price" type="number" step="0.01" className="w-28" value={a.price} onChange={(e) => set("addons", form.addons.map((x, xi) => xi === i ? { ...x, price: e.target.value } : x))} data-testid={`addon-price-${i}`} />
                        <Button variant="ghost" size="icon" className="text-destructive shrink-0" onClick={() => set("addons", form.addons.filter((_, xi) => xi !== i))} aria-label="Remove add-on"><Trash2 className="h-4 w-4" /></Button>
                      </SortableFieldRow>
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardContent className="p-6 space-y-5">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category_id} onValueChange={(v) => set("category_id", v)}>
                  <SelectTrigger data-testid="product-category-select"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                  <SelectContent>
                    {catOptions.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`category-option-${c.slug}`}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pf-order">Sort order</Label>
                <Input id="pf-order" type="number" value={form.order} onChange={(e) => set("order", e.target.value)} data-testid="product-order-input" />
                <p className="text-xs text-muted-foreground">Lower numbers appear first.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pf-stock">Stock level</Label>
                <Input id="pf-stock" type="number" min="0" value={form.stock ?? ""} onChange={(e) => set("stock", e.target.value)} placeholder="Leave empty for unlimited" data-testid="product-stock-input" />
                <p className="text-xs text-muted-foreground">Leave empty for unlimited. Website orders reduce this automatically, and at zero the product shows as out of stock.</p>
              </div>
              {[
                ["featured", "Featured product", "Shown on the homepage"],
                ["in_stock", "In stock", "Customers can order this"],
                ["active", "Active", "Visible in the shop"],
              ].map(([key, label, hint]) => (
                <div key={key} className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium">{label}</p>
                    <p className="text-xs text-muted-foreground">{hint}</p>
                  </div>
                  <Switch checked={!!form[key]} onCheckedChange={(v) => set(key, v)} data-testid={`product-${key}-switch`} />
                </div>
              ))}
            </CardContent>
          </Card>
          <Button className="w-full rounded-full" size="lg" onClick={save} disabled={saving} data-testid="save-product-btn">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create product"}
          </Button>
        </div>
      </div>
    </div>
  );
}
